import { useMemo } from "react";
import { Navigate, useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, X, ExternalLink, GitCompareArrows, ArrowRight, ArrowLeft, Sparkles, Crown, ShieldCheck } from "lucide-react";
import { COMPARISONS } from "@/data/comparisons";
import usePageSeo from "@/hooks/usePageSeo";
import AboutDppCard from "@/components/AboutDppCard";

/**
 * SEO-first head-to-head landing page: /vs/:slug
 *
 * One React page serves every DPP-vs-<competitor> comparison. Content per
 * competitor lives in `data/comparisons.js` so writing another one is a data
 * change, not a code change. Each page has:
 *   - <title> and meta description tuned for "DPP vs <competitor>" search intent
 *   - JSON-LD @graph: Article + FAQPage + BreadcrumbList
 *   - H1 + hero pitch + verdict callout
 *   - Long-form prose intro (crawlable text)
 *   - Feature-by-feature scorecard (green ticks for DPP)
 *   - "When to pick each" symmetric callouts
 *   - Full FAQ block (matched to JSON-LD)
 *   - Sticky CTAs (Try DPP free · See full pricing)
 */
function Cell({ v, isDpp }) {
  if (v === true) {
    return (
      <div className={`flex justify-center ${isDpp ? "text-primary" : "text-emerald-600 dark:text-emerald-500"}`}>
        <Check className="w-4 h-4" />
      </div>
    );
  }
  if (v === false) {
    return (
      <div className="flex justify-center text-muted-foreground/40">
        <X className="w-4 h-4" />
      </div>
    );
  }
  return (
    <div className={`text-center mono text-[10px] uppercase tracking-widest ${isDpp ? "text-primary" : "text-muted-foreground"}`}>
      {v}
    </div>
  );
}

export default function ComparisonPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const data = COMPARISONS[slug];

  const groupedRows = useMemo(() => {
    if (!data) return {};
    return data.rows.reduce((acc, r) => {
      (acc[r.area] ||= []).push(r);
      return acc;
    }, {});
  }, [data]);

  const dppWins = useMemo(() => (data?.rows || []).filter((r) => r.dpp === true).length, [data]);

  usePageSeo({
    title: data ? `${data.hero_title} — Which Dividend Tracker Wins?` : "Comparison — DPP",
    description: data
      ? `${data.hero_title}: side-by-side of pricing, backtests, AI, holdings sync & data sources.`
      : "Compare Dividend Portfolio Planner against every major dividend tracker.",
    path: data ? `/vs/${data.slug}` : "/vs",
    ogType: "article",
    jsonLdId: `comparison-jsonld-${slug || "none"}`,
    jsonLd: data ? {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": `https://dividendportfolioplanner.com/vs/${data.slug}#article`,
          headline: `${data.hero_title} — Which Dividend Tracker Wins in 2026?`,
          description: data.hero_subtitle,
          author: { "@type": "Organization", name: "Dividend Portfolio Planner" },
          publisher: {
            "@type": "Organization",
            name: "Dividend Portfolio Planner",
            logo: { "@type": "ImageObject", url: "https://dividendportfolioplanner.com/logo.png" },
          },
          datePublished: "2026-09-06",
          dateModified: "2026-09-06",
          mainEntityOfPage: `https://dividendportfolioplanner.com/vs/${data.slug}`,
          about: [
            { "@type": "Thing", name: "Dividend Portfolio Planner" },
            { "@type": "Thing", name: data.competitor_name },
          ],
        },
        {
          "@type": "FAQPage",
          "@id": `https://dividendportfolioplanner.com/vs/${data.slug}#faq`,
          mainEntity: data.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
        {
          "@type": "BreadcrumbList",
          "@id": `https://dividendportfolioplanner.com/vs/${data.slug}#breadcrumbs`,
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home",        item: "https://dividendportfolioplanner.com/" },
            { "@type": "ListItem", position: 2, name: "Compare",     item: "https://dividendportfolioplanner.com/vs" },
            { "@type": "ListItem", position: 3, name: data.hero_title, item: `https://dividendportfolioplanner.com/vs/${data.slug}` },
          ],
        },
      ],
    } : undefined,
  });

  if (!data) return <Navigate to="/pricing" replace />;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid={`comparison-page-${data.slug}`}>
      {/* Sticky "back to Compare hub" nav — keeps users one click away from
          the sibling scoreboards without having to scroll to the bottom of
          the page or reopen the hamburger. Rendered at the top of every
          `/vs/:slug` page. */}
      <div
        className="sticky top-0 z-30 -mx-6 px-6 py-3 mb-6 border-b border-border/70 bg-background/85 backdrop-blur"
        data-testid="comparison-back-nav"
      >
        <div className="flex items-center justify-between gap-4">
          <Link
            to="/vs"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            data-testid="comparison-back-to-hub"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Compare
          </Link>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground truncate">
            DPP vs {data.competitor_name}
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.25em] text-secondary">
          <GitCompareArrows className="w-3.5 h-3.5" /> Head to head
        </div>
        <h1 className="serif text-4xl sm:text-5xl lg:text-6xl mt-3" data-testid="comparison-hero-title">
          {data.hero_title}
        </h1>
        <p className="text-muted-foreground mt-4 text-base leading-relaxed">{data.hero_subtitle}</p>

        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button className="rounded-full" onClick={() => nav("/compare")} data-testid="comparison-cta-signup">
            <GitCompareArrows className="w-4 h-4 mr-2" /> Open the Compare Chart
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => nav("/pricing")}>
            See full pricing <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <div className="mt-6 inline-flex items-center gap-2 mono text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20">
          <Crown className="w-3.5 h-3.5" /> {data.verdict}
        </div>
      </div>

      {/* Prose intro */}
      <div className="mt-14 prose prose-neutral dark:prose-invert max-w-3xl mx-auto text-sm sm:text-base leading-relaxed">
        <p>{data.prose_intro}</p>
      </div>

      {/* Scorecard */}
      <Card className="mt-12" data-testid="comparison-scorecard">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-1">
            <GitCompareArrows className="w-4 h-4 text-secondary" />
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-secondary">Scorecard</div>
          </div>
          <h2 className="serif text-2xl sm:text-3xl">Feature-by-feature</h2>
          <p className="text-sm text-muted-foreground mt-1">
            <span className="text-foreground font-medium">DPP wins {dppWins} of {data.rows.length} categories</span> — checked against public pricing pages and support docs as of Q1 2026.
          </p>

          <div className="mt-6 overflow-x-auto">
            <div className="min-w-[560px]">
              <div className="grid grid-cols-[minmax(240px,1fr)_minmax(120px,1fr)_minmax(120px,1fr)] gap-2 pb-3 border-b border-border">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Capability</div>
                <div className="text-center">
                  <div className="serif text-lg text-primary">DPP <span className="ml-1 mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-primary/10 text-primary align-middle">You</span></div>
                </div>
                <div className="text-center">
                  <div className="serif text-lg">{data.competitor_name}</div>
                </div>
              </div>

              {Object.entries(groupedRows).map(([area, rows]) => (
                <div key={area} className="border-b border-border/60">
                  <div className="mono text-[10px] uppercase tracking-widest text-secondary/80 pt-3 pb-1">{area}</div>
                  {rows.map((r) => (
                    <div
                      key={r.name}
                      className={`grid grid-cols-[minmax(240px,1fr)_minmax(120px,1fr)_minmax(120px,1fr)] gap-2 py-2 items-center border-t border-border/40 text-sm ${r.dpp === true ? "hover:bg-primary/[0.03]" : ""}`}
                      data-testid={`comparison-row-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
                    >
                      <div className="text-foreground/85 pr-2">{r.name}</div>
                      <Cell v={r.dpp} isDpp />
                      <Cell v={r.competitor} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* When to pick each */}
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-primary/30" data-testid="when-dpp">
          <CardContent className="p-6">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-primary" />
              <h3 className="serif text-xl">Pick DPP if…</h3>
            </div>
            <p className="text-sm leading-relaxed mt-3 text-foreground/85">{data.prose_when_dpp}</p>
          </CardContent>
        </Card>
        <Card data-testid="when-competitor">
          <CardContent className="p-6">
            <h3 className="serif text-xl">Pick {data.competitor_name} if…</h3>
            <p className="text-sm leading-relaxed mt-3 text-foreground/85">{data.prose_when_snowball || data.prose_when_sharesight || data.prose_when_seekingalpha}</p>
            <a href={data.competitor_url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors mt-3">
              Visit {data.competitor_name} <ExternalLink className="w-3 h-3" />
            </a>
          </CardContent>
        </Card>
      </div>

      {/* FAQ */}
      <Card className="mt-12" data-testid="comparison-faq">
        <CardContent className="p-6 sm:p-8">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-secondary">Frequently asked</div>
          <h2 className="serif text-2xl sm:text-3xl mt-1">Answers for shoppers</h2>
          <div className="mt-5 space-y-4">
            {data.faq.map((f, i) => (
              <details key={i} className="rounded-md border border-border p-4 group open:bg-muted/20" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer font-medium text-foreground/90 flex items-center justify-between">
                  <span>{f.q}</span>
                  <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground ml-2 shrink-0">Answer</span>
                </summary>
                <p className="text-sm text-foreground/80 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Bottom CTA */}
      <div className="mt-14 text-center">
        <div className="mono text-[10px] uppercase tracking-[0.25em] text-secondary">Ready to see side-by-side?</div>
        <h2 className="serif text-3xl sm:text-4xl mt-2">Open the DPP Compare Chart.</h2>
        <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
          Load any two portfolios into the Compare Chart and see the 30-year cumulative-return line, dividend income growth, drawdown, and CAGR side-by-side. Free — 1 saved portfolio + 30+ year backtests forever.
        </p>
        <div className="mt-5 flex justify-center gap-3 flex-wrap">
          <Button className="rounded-full" onClick={() => nav("/compare")} data-testid="comparison-cta-signup-bottom">
            <GitCompareArrows className="w-4 h-4 mr-2" /> Open Compare Chart
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => nav("/pricing")}>
            See full pricing <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
        <div className="mt-8 flex justify-center gap-3 flex-wrap text-xs text-muted-foreground">
          <span>Also compare:</span>
          {Object.values(COMPARISONS).filter((c) => c.slug !== data.slug).map((c) => (
            <Link key={c.slug} to={`/vs/${c.slug}`} className="underline underline-offset-2 hover:text-primary" data-testid={`other-comparison-${c.slug}`}>
              DPP vs {c.competitor_name}
            </Link>
          ))}
        </div>
      </div>

      <AboutDppCard />
    </div>
  );
}
